import React, { Suspense, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLazyLoadQuery } from 'react-relay/hooks';

import { withStyles, WithStyles } from '@material-ui/core/styles';
import { graphql } from 'babel-plugin-relay/macro';
import classnames from 'classnames';
import { useFilterMessagesContext } from 'contexts';
import { ErrorBoundary, Typography } from 'embeds/components';
import {
  messagesTabsChatActivities,
  TabsChatActivities,
  TabsChatActivitiesProps,
  TabsChatActivitiesTab,
} from 'embeds/deprecated/fragments';
import { compact, defaultsDeep, get, includes, isEmpty, map, size, split } from 'lodash';
import { useGroupByIdsV2 } from 'ui-model';

import { selectCurrentMasterSettings } from '@lib/selectors';
import { addQuery, removeQuery } from '@lib/services/queryService';

import { WrapperQueryListChatActivities_EmbedQuery } from './__generated__/WrapperQueryListChatActivities_EmbedQuery.graphql';
import { WrapperQueryListChatActivitiesRoot_EmbedQuery } from './__generated__/WrapperQueryListChatActivitiesRoot_EmbedQuery.graphql';
import FiltersChatActivities, { FiltersChatActivitiesProps } from './FiltersChatActivities';
import QueryListChatActivities, { QueryListChatActivitiesProps } from './QueryListChatActivities';
import QueryTextFieldSearchPatient from './QueryTextFieldSearchPatient';
import styles from './wrapperQueryListChatActivities.styles';

interface WrapperQueryListChatActivitiesProps
  extends WithStyles<typeof styles>,
    Omit<
      QueryListChatActivitiesProps,
      'classes' | 'patients' | 'variables' | 'filtersCount' | 'onFiltersOpen' | 'onFiltersClear'
    >,
    React.HTMLAttributes<HTMLDivElement> {
  header?: string;
  includeTabs?: TabsChatActivitiesProps['includeTabs'];
  onTabChange: Function;
  selectedPatient: any;
  selectedTab: TabsChatActivitiesTab;
}

const WrapperQueryListChatActivities: React.FC<WrapperQueryListChatActivitiesProps> = ({
  classes,
  className,
  header,
  includeTabs,
  onTabChange,
  selectedPatient,
  selectedTab,
  ...rest
}) => {
  const settings = useSelector(selectCurrentMasterSettings);
  const isToUseSendAndReceiverFilters = settings?.communication?.filterMessagesBySenderAndReceiver;
  const { filters: filtersMessages, selectedFacilitiesAndFacilitiesFromGroups } = useFilterMessagesContext();
  const { facilities: selectedFacilities, groups: selectedGroupsIds, me: isToFilterMessagesByMe } = filtersMessages;
  const { data: groups = [] } = useGroupByIdsV2({ ids: selectedGroupsIds }, { enabled: selectedGroupsIds?.length > 0 });
  const selectedGroupsFacilitiesIds = new Set(groups?.map((item) => item.facilities).flat());
  const isToFilterByGroups = selectedGroupsIds.length > 0 && !isToFilterMessagesByMe;
  const facilities = isToFilterByGroups ? [...selectedGroupsFacilitiesIds] : selectedFacilities;

  const dataRoot = useLazyLoadQuery<WrapperQueryListChatActivitiesRoot_EmbedQuery>(
    graphql`
      query WrapperQueryListChatActivitiesRoot_EmbedQuery {
        viewer {
          _id
          oid_groups
        }
      }
    `,
    {},
    { fetchPolicy: 'store-or-network' },
  );
  const viewer = get(dataRoot, 'viewer')!;

  const { formatMessage } = useIntl();
  const [patients, setPatients] = useState<any>([]);
  const includeFilterAssignees = includes([TabsChatActivitiesTab.Assigned, TabsChatActivitiesTab.Closed], selectedTab);
  // Obtain initial filters from the URL
  const urlParams = new URLSearchParams(window.location.search);
  const [filters, setFilters] = useState({
    assignees: compact(split(urlParams.get('assignees') || '', ',')),
    facilities: compact(split(urlParams.get('facilities') || '', ',')),
  });
  const hub9000FiltersCount = isToFilterMessagesByMe ? 1 : size(isToFilterByGroups ? groups : facilities);
  let filtersCount = isToUseSendAndReceiverFilters ? hub9000FiltersCount : size(filters.facilities);
  if (includeFilterAssignees) {
    filtersCount += size(filters.assignees);
  }

  const [patientsFilter, setPatientsFilter] = useState('');
  const [filterOptionOn, setFilterOptionOn] = useState(false);
  const includeAssignee = !includes([TabsChatActivitiesTab.Unassigned], selectedTab);
  const variables: any = { filter: {}, includeAssignee };

  if (patientsFilter) {
    variables.filter = { _operators: { patient: { in: map(patients, '_id') } } };
  } else {
    let _operators;
    if (
      (isToUseSendAndReceiverFilters && !isEmpty(selectedFacilitiesAndFacilitiesFromGroups)) ||
      !isEmpty(filters.facilities)
    ) {
      _operators = defaultsDeep({}, _operators, {
        facilities: {
          in: isToUseSendAndReceiverFilters ? selectedFacilitiesAndFacilitiesFromGroups : filters.facilities,
        },
      });
    }
    switch (selectedTab) {
      case TabsChatActivitiesTab.MyChats:
        variables.filter = {
          automated: false,
          status: 'assigned',
          _operators,
          OR: compact([
            { assignee: viewer._id },
            !isEmpty(viewer.oid_groups) && { _operators: { groupAssignee: { in: viewer.oid_groups } } },
          ]),
        };
        break;
      case TabsChatActivitiesTab.Unassigned:
        variables.filter = {
          automated: false,
          status: 'unassigned',
          _operators,
        };
        break;
      case TabsChatActivitiesTab.Assigned: {
        variables.filter = {
          automated: false,
          status: 'assigned',
          _operators,
          OR: !isEmpty(filters.assignees)
            ? [
                { _operators: { assignee: { in: filters.assignees } } },
                { _operators: { groupAssignee: { in: filters.assignees } } },
              ]
            : undefined,
        };
        break;
      }
      case TabsChatActivitiesTab.Closed: {
        variables.filter = {
          automated: false,
          status: 'closed',
          _operators,
          OR: size(filters.assignees)
            ? [
                { _operators: { assignee: { in: filters.assignees } } },
                { _operators: { groupAssignee: { in: filters.assignees } } },
              ]
            : undefined,
        };
        break;
      }
      default:
    }
  }

  const data = useLazyLoadQuery<WrapperQueryListChatActivities_EmbedQuery>(
    graphql`
      query WrapperQueryListChatActivities_EmbedQuery {
        ...TabsChatActivities_EmbedQuery
      }
    `,
    {},
    { fetchPolicy: 'store-or-network' },
  );

  const handleShowFilters = useCallback(() => {
    setFilterOptionOn(true);
  }, [setFilterOptionOn]);

  const handleFiltersCancel = useCallback(() => {
    setFilterOptionOn(false);
  }, [setFilterOptionOn]);

  const handleFiltersClear = useCallback(() => {
    setFilters({ assignees: [], facilities: [] });
    setPatientsFilter('');

    // Remove filters applied from the URL
    removeQuery('assignees', 'facilities');
  }, [setFilters]);

  const handleFiltersChange = useCallback(
    (newFilters: FiltersChatActivitiesProps['filters']) => {
      setFilters(newFilters);
      setFilterOptionOn(false);
      setPatientsFilter('');

      // Update URL with filters applied
      addQuery({
        assignees: newFilters.assignees.join(','),
        facilities: newFilters.facilities.join(','),
      });
    },
    [setFilters, setFilterOptionOn],
  );

  return (
    <>
      <div
        className={classnames(classes.root, className, {
          [classes.rootHidden]: filterOptionOn,
        })}
      >
        {header && (
          <Typography className={classes.title} variant="customHeader" color="primary">
            {header}
          </Typography>
        )}

        <div className={classes.searchWrapper}>
          <div className={classes.searchItems}>
            <QueryTextFieldSearchPatient
              value={patientsFilter}
              fullWidth
              size="small"
              onChange={(search, newPatients) => {
                setPatientsFilter(search);
                setPatients(newPatients);
              }}
            />
          </div>
        </div>
        {!patientsFilter && (
          <TabsChatActivities
            fragmentRef={data}
            includeTabs={includeTabs}
            value={selectedTab}
            onChange={(_, value) => onTabChange(value)}
          />
        )}
        <QueryListChatActivities
          variables={variables}
          patients={patientsFilter ? patients : undefined}
          filtersCount={filtersCount}
          onFiltersOpen={handleShowFilters}
          onFiltersClear={handleFiltersClear}
          selectedTab={selectedTab}
          selectedPatient={selectedPatient}
          {...rest}
        />
      </div>
      {filterOptionOn && (
        <FiltersChatActivities
          filters={filters}
          title={`${formatMessage(messagesTabsChatActivities[selectedTab])} - Filters`}
          includeFilterAssignees={includeFilterAssignees}
          filterChatActivityCount={{ status: selectedTab === TabsChatActivitiesTab.Closed ? 'closed' : 'assigned' }}
          onChange={handleFiltersChange}
          onClear={handleFiltersClear}
          onCancel={handleFiltersCancel}
        />
      )}
    </>
  );
};

export default withStyles(styles)((props: WrapperQueryListChatActivitiesProps) => (
  <ErrorBoundary>
    <Suspense fallback={null}>
      <WrapperQueryListChatActivities {...props} />
    </Suspense>
  </ErrorBoundary>
));
